import React from 'react';
import { FastField, Field, type FieldProps } from 'formik';
import KoobInputToggle from '../KoobInputToggle';

type KoobInputToggleFieldProps = {
  name: string;
  label?: React.ReactNode;
  disabled?: boolean;
  size?: 'sm' | 'md';
  /** Show the error before the field is touched when false */
  touchRequired?: boolean;
  /** Use formik FastField (skips re-renders of untouched fields) */
  fast?: boolean;
  /** Display as unchecked while disabled, without touching the form value */
  resetOnDisable?: boolean;
  /** Side-effect fired after the value is committed to the form */
  onChange?: (value: boolean) => void;
  /** @deprecated use data-testid on KoobInputToggle — kept for `-enabled`/`-disabled` selectors */
  testId?: string;
};

export const KoobInputToggleField = ({
  name,
  label,
  disabled,
  size,
  touchRequired = true,
  fast = false,
  resetOnDisable = false,
  onChange,
  testId,
}: KoobInputToggleFieldProps) => {
  const FieldComponent = fast ? FastField : Field;

  return (
    <FieldComponent name={name}>
      {({ field, meta, form }: FieldProps) => {
        const hasError = touchRequired
          ? Boolean(meta.error && meta.touched)
          : Boolean(meta.error);
        const checked =
          disabled && resetOnDisable ? false : Boolean(field.value);

        return (
          <div className="flex flex-col gap-1">
            <KoobInputToggle
              name={name}
              label={label}
              checked={checked}
              onCheckedChange={(value) => {
                form.setFieldValue(name, value);
                onChange?.(value);
              }}
              onBlur={field.onBlur}
              disabled={disabled}
              size={size}
              error={hasError}
              testId={testId ?? `toggle-${name}`}
            />
            {hasError && (
              <p className="m-0 text-sm text-red-500">{meta.error}</p>
            )}
          </div>
        );
      }}
    </FieldComponent>
  );
};

export default KoobInputToggleField;
